import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { getOrdersByUser } from '@services/sm-api.js'

import V2StatusPill from '../V2StatusPill'
import { INTENTS, MAIN_CHIPS, TICKET_PATH, contextChips, greeting, matchIntent } from './intents'
import '../../../css/v2.css'

// Assistente do portal (só sob /v2, fora do espelho /v2/admin) — substitui o ícone
// flutuante do WhatsApp por um mini chat guiado. Regras:
//  - vive fora das <Routes>, então a conversa sobrevive à navegação entre telas;
//  - só lê estado (sessão, pedido ativo) e consulta orders/{id}/list direto no
//    serviço, sem despachar nada no redux — as telas V2 não percebem o bot;
//  - "orientação dinâmica": destaca o elemento pedido na tela (v2-assist-spotlight),
//    navegando antes se for preciso; no mobile o painel fecha para não cobrir;
//  - sem resolver em 2 tentativas de texto livre, oferece o chamado da conta
//    (revisão GCA de 29/09: sem WhatsApp nem 0800 no assistente);
//  - eventos para o GTM sem dado pessoal (intent + rota).
const MAX_FALLBACKS = 2
const EMPTY_USER = {}
let seq = 0
const nextId = () => `m${++seq}`

const track = (intent, pathname) => {
  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'smd_assistant', assistant_intent: intent, assistant_page: pathname.replace(/\/\d+/g, '/:id') })
    }
  } catch (e) {
    /* analytics nunca quebra o bot */
  }
}

// destaca o elemento (espera até 6s ele existir, útil logo após navegar)
const spotlight = selector => {
  const started = Date.now()
  const tick = () => {
    const el = document.querySelector(selector)
    if (el) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' })
      el.classList.add('v2-assist-spotlight')
      setTimeout(() => el.classList.remove('v2-assist-spotlight'), 4000)
      return
    }
    if (Date.now() - started < 6000) setTimeout(tick, 200)
  }
  tick()
}

const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.2-4.2A8 8 0 1 1 21 12z" />
    <circle cx="9" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="13" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="17" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
)

const BotAvatar = () => (
  <span className="v2-assist-avatar" aria-hidden="true">
    SM
  </span>
)

export default function V2Assistant() {
  const navigate = useNavigate()
  const location = useLocation()
  const authenticated = useSelector(state => !!(state.sessionReducer && state.sessionReducer.authenticated))
  const user = useSelector(state => (state.sessionReducer && state.sessionReducer.user) || EMPTY_USER)
  const activeOrder = useSelector(state => state.order && state.order.active)

  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [typing, setTyping] = useState(false)
  const [input, setInput] = useState('')
  const [fallbacks, setFallbacks] = useState(0)
  const [seen, setSeen] = useState(false)
  const listRef = useRef(null)
  const inputRef = useRef(null)

  const pathname = location.pathname
  const hidden = pathname.startsWith('/v2/admin')
  const detailMatch = pathname.match(/^\/v2\/conta\/pedidos\/(\d+)/)
  const contextOrderId = detailMatch ? detailMatch[1] : null

  const ctx = useCallback(
    () => ({
      pathname,
      authenticated,
      user,
      activeOrder,
      contextOrderId,
      fetchOrders: () => getOrdersByUser({ id: user.id, page: 1 }),
    }),
    [pathname, authenticated, user, activeOrder, contextOrderId]
  )

  const push = useCallback(list => setMessages(prev => prev.concat(list.map(m => ({ id: nextId(), ...m })))), [])

  const greet = useCallback(() => {
    const c = ctx()
    const extra = contextChips(c)
    push([{ from: 'bot', text: greeting(c), chips: extra.concat(MAIN_CHIPS) }])
  }, [ctx, push])

  const runIntent = useCallback(
    async (intentId, label) => {
      const intent = INTENTS[intentId]
      if (!intent) return
      if (label) push([{ from: 'user', text: label }])
      track(intentId, pathname)
      setTyping(true)
      try {
        const out = await intent.respond(ctx())
        // pequena pausa para a resposta não "piscar" antes da pergunta
        await new Promise(r => setTimeout(r, 350))
        push(out)
      } finally {
        setTyping(false)
      }
      if (intentId !== 'humano') setFallbacks(0)
    },
    [ctx, pathname, push]
  )

  const runAction = useCallback(
    action => {
      if (action.kind === 'navigate') {
        push([{ from: 'user', text: action.label }])
        navigate(action.to, action.to === '/v2/conta/entrar' ? { state: { returnTo: '/v2/conta/pedidos' } } : undefined)
        if (window.innerWidth <= 640) setOpen(false)
      } else if (action.kind === 'spotlight') {
        push([{ from: 'user', text: action.label }, { from: 'bot', text: 'Vou destacar na tela para você. Toque no botão de ajuda para voltar à conversa.' }])
        track(`spotlight:${action.selector}`, pathname)
        if (action.path && action.path !== pathname) navigate(action.path)
        setOpen(false)
        setTimeout(() => spotlight(action.selector), 150)
      } else if (action.kind === 'ticket') {
        // chamado da área da conta; sem sessão passa pelo login e volta para o chamado
        push([{ from: 'user', text: action.label }])
        track('ticket', pathname)
        if (authenticated) navigate(TICKET_PATH)
        else navigate('/v2/conta/entrar', { state: { returnTo: TICKET_PATH } })
        if (window.innerWidth <= 640) setOpen(false)
      } else if (action.kind === 'link') {
        track('link', pathname)
        window.open(action.href, '_blank', 'noopener')
      }
    },
    [authenticated, navigate, pathname, push]
  )

  const onChip = c => (c.intent ? runIntent(c.intent, c.label) : runAction(c))

  const submitText = e => {
    e.preventDefault()
    const text = input.trim()
    if (!text || typing) return
    setInput('')
    const intentId = matchIntent(text)
    if (intentId) {
      push([{ from: 'user', text }])
      runIntent(intentId)
      return
    }
    push([{ from: 'user', text }])
    track('fallback', pathname)
    const n = fallbacks + 1
    setFallbacks(n)
    if (n >= MAX_FALLBACKS) {
      push([
        {
          from: 'bot',
          text: 'Não consegui resolver por aqui. Abra um chamado e nossa equipe entrará em contato, se necessário.',
          actions: [{ label: 'Abrir um chamado', kind: 'ticket' }],
          chips: [{ label: 'Ver os assuntos', intent: 'menu' }],
        },
      ])
    } else {
      push([{ from: 'bot', text: 'Não entendi bem. Você pode escolher um assunto abaixo ou escrever de outro jeito (por exemplo: "onde está meu pedido" ou "meu voucher expirou").', chips: MAIN_CHIPS }])
    }
  }

  const toggle = () => {
    setOpen(o => !o)
    setSeen(true)
  }

  // saudação na primeira abertura
  useEffect(() => {
    if (open && !messages.length) greet()
  }, [open, messages.length, greet])

  // rolagem para o fim e foco no input
  useEffect(() => {
    if (!open) return
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
    if (inputRef.current && window.innerWidth > 640) inputRef.current.focus()
  }, [messages, typing, open])

  // integração: qualquer tela pode abrir o bot (opcionalmente numa intenção)
  useEffect(() => {
    const handler = e => {
      setOpen(true)
      setSeen(true)
      const intent = e.detail && e.detail.intent
      if (intent) setTimeout(() => runIntent(intent, e.detail.label), 50)
    }
    window.addEventListener('smd:assistant', handler)
    return () => window.removeEventListener('smd:assistant', handler)
  }, [runIntent])

  // ESC fecha
  useEffect(() => {
    if (!open) return
    const onKey = e => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  if (hidden) return null

  return (
    <div className="v2-assist" data-open={open ? '1' : '0'}>
      {open && (
        <section className="v2-assist-panel" role="dialog" aria-label="Assistente do SM Direto" id="v2-assist-panel">
          <header className="v2-assist-head">
            <BotAvatar />
            <div className="v2-assist-head__text">
              <strong>Assistente SM Direto</strong>
              <span>Voucher, pedido, pagamento e entrega</span>
            </div>
            <button type="button" className="v2-assist-close" id="v2-assist-close" aria-label="Fechar" onClick={toggle}>
              ×
            </button>
          </header>

          <div className="v2-assist-list" ref={listRef} id="v2-assist-list">
            {messages.map(m => (
              <div key={m.id} className={`v2-assist-msg v2-assist-msg--${m.from}`}>
                {m.from === 'bot' && <BotAvatar />}
                <div className="v2-assist-msg__body">
                  <div className="v2-assist-bubble">{m.text}</div>
                  {m.orders && (
                    <div className="v2-assist-orders">
                      {m.orders.map(o => (
                        <div key={o.id} className="v2-assist-order">
                          <div>
                            <strong>Pedido SMD-{o.id}</strong>
                            <span>{o.created}</span>
                            <V2StatusPill {...o.status} code={o.code} />
                          </div>
                          <button type="button" onClick={() => runAction({ kind: 'navigate', label: `Ver pedido SMD-${o.id}`, to: `/v2/conta/pedidos/${o.id}` })}>
                            Ver detalhes
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {m.actions && m.actions.length > 0 && (
                    <div className="v2-assist-actions">
                      {m.actions.map((a, i) => (
                        <button key={i} type="button" className="v2-assist-action" onClick={() => runAction(a)}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {m.chips && m.chips.length > 0 && (
                    <div className="v2-assist-chips">
                      {m.chips.map((c, i) => (
                        <button key={i} type="button" className="v2-assist-chip" onClick={() => onChip(c)}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {typing && (
              <div className="v2-assist-msg v2-assist-msg--bot">
                <BotAvatar />
                <div className="v2-assist-bubble v2-assist-typing" aria-label="digitando">
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            )}
          </div>

          <form className="v2-assist-input" onSubmit={submitText}>
            <input
              ref={inputRef}
              id="v2-assist-input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Escreva sua dúvida…"
              maxLength={200}
              autoComplete="off"
            />
            <button type="submit" id="v2-assist-send" aria-label="Enviar" disabled={!input.trim() || typing}>
              ➤
            </button>
          </form>
          <button type="button" className="v2-assist-human" id="v2-assist-human" onClick={() => runAction({ label: 'Abrir um chamado', kind: 'ticket' })}>
            Precisa de mais ajuda? Abrir um chamado
          </button>
        </section>
      )}

      <button type="button" className="v2-assist-fab" id="v2-assist-fab" onClick={toggle} aria-expanded={open} aria-controls="v2-assist-panel" title="Ajuda">
        <ChatIcon />
        <span className="v2-assist-fab__label">{open ? 'Fechar' : 'Ajuda'}</span>
        {!seen && <span className="v2-assist-fab__dot" aria-hidden="true" />}
      </button>
    </div>
  )
}
